import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { SUBZONES_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import ZoneSubzoneFormDialog from "./ZoneSubzoneFormDialog";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import ZoneSearchInput from "./ZonesSearchInput";
import { formatWithTzLabel } from "../../utils/dateUtils";

const columns = [
  { id: "name", label: "Subzone" },
  { id: "zone", label: "Zone", render: (s) => s.zone?.name },
  { id: "created_by", label: "Created By", render: (s) => s.created_by?.name },
  {
    id: "created_at",
    label: "Created On",
    render: (s) =>
      s.created_at ? formatWithTzLabel(s.created_at, "DD/MM/YYYY") : "",
  },
  { id: "updated_by", label: "Updated By", render: (s) => s.updated_by?.name },
  {
    id: "updated_at",
    label: "Updated On",
    render: (s) =>
      s.updated_at ? formatWithTzLabel(s.updated_at, "DD/MM/YYYY") : "",
  },
];

export default function UsersTable({
  refreshKey,
  setRefreshKey,
  searchInput,
  updateSearchValue,
  updateRefreshKey,
}) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [subzonesData, setUsersData] = useState([]);
  const [subzoneModalState, setsubzoneModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
    currentForm: "subzone",
  });
  const navigate = useNavigate();

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
    serverSidePage: 0,
  });

  useEffect(() => {
    setController((prev) => {
      if (!searchInput) return prev;
      if (prev.page === 0 && prev.serverSidePage === 0) return prev;
      return { ...prev, page: 0, serverSidePage: 0 };
    });
  }, [searchInput]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const queryParams = new URLSearchParams({
          limit: controller.rowsPerPage.toString(),
          offset: (
            controller.serverSidePage * controller.rowsPerPage
          ).toString(),
          search_id: searchInput,
        });
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-tenant-ID": tenant,
        };
        const response = await fetch(
          `${SUBZONES_ENDPOINT}?${queryParams.toString()}`,
          { headers },
        );
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === "user_inactive") {
            auth.logOut();
            navigate("/login");
          }
          throw new Error("Failed to fetch data");
        }
        const jsonData = await response.json();
        setUsersData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchInput,
  ]);

  const handleEditClick = (subzone) => {
    setsubzoneModalState({
      isOpen: true,
      selectedClient: subzone,
      mode: "edit",
    });
  };

  const tableColumns = [
    ...columns,
    {
      id: "action",
      label: "Action",
      align: "center",
      render: (s) => (
        <ButtonOne variant="contained" onClick={() => handleEditClick(s)}>
          Edit
        </ButtonOne>
      ),
    },
  ];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        <ZoneSearchInput
          updateSearchValue={updateSearchValue}
          updateRefreshKey={setRefreshKey}
        />
        <ButtonOne
          variant="contained"
          onClick={() =>
            setsubzoneModalState({
              isOpen: true,
              selectedClient: null,
              mode: "add",
            })
          }
          sx={{ margin: 2 }}
        >
          Add subzone
        </ButtonOne>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={subzonesData}
        rowKey={(s, i) => s.name ?? i}
        emptyMessage="No match found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) =>
          setController((prev) => ({
            ...prev,
            page: newPage,
            serverSidePage: newPage,
          }))
        }
        onRowsPerPageChange={(e) =>
          setController((prev) => ({
            ...prev,
            rowsPerPage: parseInt(e.target.value, 10),
            page: 0,
            serverSidePage: 0,
          }))
        }
      />

      <ZoneSubzoneFormDialog
        modalState={subzoneModalState}
        handleModalState={setsubzoneModalState}
        selectedClient={subzoneModalState.selectedClient}
        refreshTable={setRefreshKey}
        mode={subzoneModalState.mode}
        currentForm={subzoneModalState.currentForm}
      />
    </Paper>
  );
}
